import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { cls } from "app/utils";

import styles from "./CoachTabs.module.scss";

export type CoachTab = "overview" | "private" | "packages" | "events";

export const COACH_TABS: CoachTab[] = [
  "overview",
  "private",
  "packages",
  "events",
];

/** Where a tab lives under the page's own route. Overview is the route itself. */
export const coachTabPath = (basePath: string, tab: CoachTab): string =>
  tab === "overview" ? basePath : `${basePath}/${tab}`;

interface Props {
  active: CoachTab;
  /**
   * The page's own route, when it has one. With it the tabs are links, so a tab
   * can be shared and reloaded; without it they are buttons over local state,
   * which is what the mount inside the facility booking flow needs — that page
   * owns the URL, and this is a section of it.
   */
  basePath?: string;
  onSelect: (tab: CoachTab) => void;
}

/**
 * The four tabs under the coach's bio (frame 43592:26426).
 *
 * Same four the public landing page carries, in the same order, so a coach's page
 * reads the same whether it is opened from a link or from the booking flow.
 */
const CoachTabs: React.FC<Props> = ({ active, basePath, onSelect }) => {
  const { t } = useTranslation();

  const label: Record<CoachTab, string> = {
    overview: t("Overview"),
    private: t("Private Sessions"),
    packages: t("Packages & Subscriptions"),
    events: t("Events & Clinics"),
  };

  return (
    <div className={styles.tabs} role="tablist" data-testid="coach-tabs">
      {COACH_TABS.map((tab) => {
        const className = cls(styles.tab, {
          [styles.tab_active]: tab === active,
        });

        /**
         * CFTB-3473's rule, which the public page's tabs already follow:
         * `replace`, not push. The four are views of one page, so pushing made
         * each a history entry and the back button then walked the tabs instead
         * of leaving the page.
         */
        return basePath ? (
          <Link
            key={tab}
            replace
            to={coachTabPath(basePath, tab)}
            className={className}
            role="tab"
            aria-selected={tab === active}
            data-testid={`coach-tab-${tab}`}
          >
            {label[tab]}
          </Link>
        ) : (
          <button
            key={tab}
            type="button"
            className={className}
            role="tab"
            aria-selected={tab === active}
            onClick={() => onSelect(tab)}
            data-testid={`coach-tab-${tab}`}
          >
            {label[tab]}
          </button>
        );
      })}
    </div>
  );
};

export default CoachTabs;
